import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';

import { getParticipation, addParticipants, removeParticipant } from '../../../services/surveyService';
import { parseTableParams, formatDateTime } from '../../../utils/Utils';
import DataTable from '../../../components/ui/DataTable';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import PersonCell from '../../../components/ui/PersonCell';
import AsyncGenericSelect from '../../../components/ui/AsyncGenericSelect';
import Button from '../../../components/ui/Button';
import api from '../../../services/api';

const Container = styled.div`
  height: 100%;
  overflow: hidden;
`;

const DrawerHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px; border-bottom: 1px solid #E8ECF0;
`;
const DrawerTitle = styled.h3`
  font-size: 16px; font-weight: 700; color: #004242; font-family: 'Nunito Sans', sans-serif;
`;
const CloseBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border: none; border-radius: 4px;
  background: none; color: #A0A8B4; cursor: pointer;
  &:hover { background: #F5F7FA; }
`;
const DrawerBody = styled.div`
  flex: 1; overflow-y: auto; padding: 24px;
  display: flex; flex-direction: column; gap: 20px;
  & > div:nth-child(1) { z-index: 104; }
  & > div:nth-child(2) { z-index: 103; }
  & > div:nth-child(3) { z-index: 102; }
  & > div:nth-child(4) { z-index: 101; }
`;
const DrawerFooter = styled.div`
  display: flex; justify-content: flex-end; gap: 12px;
  padding: 16px 24px; border-top: 1px solid #E8ECF0;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $status }) => $status === 'completed' ? '#16a34a15' : '#ca8a0415'};
  color: ${({ $status }) => $status === 'completed' ? '#16a34a' : '#ca8a04'};
`;

export default function SurveyParticipationTab({ surveyId, surveyStatus, containerHeight }) {
  const { t, i18n } = useTranslation();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 0, pageSize: 10 });
  const [fetching, setFetching] = useState(true);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState({ isOpen: false, userId: null, name: '' });
  const [removing, setRemoving] = useState(false);

  const [selEmployees, setSelEmployees] = useState([]);
  const [selDepartments, setSelDepartments] = useState([]);
  const [selLocations, setSelLocations] = useState([]);
  const [selUserGroups, setSelUserGroups] = useState([]);

  const fetchData = useCallback((params) => {
    setFetching(true);
    getParticipation(surveyId, params)
      .then((res) => {
        const d = res.data?.data;
        if (d?.data) {
          setData(d.data);
          setPagination({
            totalRowCount: d.totalRowCount || 0,
            pageIndex: (d.pageIndex || 1) - 1,
            pageSize: d.pageSize || 10,
          });
        } else {
          setData([]);
          setPagination({ totalRowCount: 0, pageIndex: 0, pageSize: 10 });
        }
      })
      .catch(() => {})
      .finally(() => setFetching(false));
  }, [surveyId]);

  useEffect(() => { fetchData(queryParams); }, [fetchData, queryParams]);

  const canEdit = surveyStatus === 'active';

  const baseColumns = useMemo(() => [
    {
      name: 'userFullName',
      label: t('employee.fullName'),
      options: {
        filter: true, sort: true, filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <PersonCell name={value || '-'} profilePicture={row?.userProfilePicture} />;
        },
      },
    },
    {
      name: 'departmentName',
      label: t('department.title'),
      options: { filter: false, sort: false },
    },
    {
      name: 'status',
      label: t('survey.status'),
      options: {
        filter: true, sort: true,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['completed', 'pending'],
          renderValue: (v) => v === 'completed' ? t('survey.completed') : t('survey.pending'),
        },
        customFilterListOptions: {
          render: (v) => v === 'completed' ? t('survey.completed') : t('survey.pending'),
        },
        customBodyRender: (value) => (
          <StatusBadge $status={value}>
            {value === 'completed' ? t('survey.completed') : t('survey.pending')}
          </StatusBadge>
        ),
      },
    },
    {
      name: 'completedAt',
      label: t('survey.completedDate', { defaultValue: 'Tamamlanma Tarihi' }),
      options: {
        filter: false, sort: true,
        customBodyRender: (value) => formatDateTime(value, i18n.language),
      },
    },
  ], [t, data]);

  const handleRemove = useCallback((row) => {
    setRemoveConfirm({ isOpen: true, userId: row.userId, name: row.userFullName });
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    setRemoving(true);
    try {
      await removeParticipant(surveyId, removeConfirm.userId);
      setRemoveConfirm({ isOpen: false, userId: null, name: '' });
      fetchData(queryParams);
    } catch { /* interceptor */ }
    setRemoving(false);
  }, [surveyId, removeConfirm.userId, fetchData, queryParams]);

  const actionOptions = useMemo(() => {
    if (!canEdit) return [];
    return [
      {
        type: 'delete',
        label: t('common.delete'),
        onClick: handleRemove,
        condition: (row) => row.status !== 'completed',
      },
    ];
  }, [canEdit, t, handleRemove]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  // Add participants drawer
  const searchEmployees = useCallback(async (q) => {
    const params = q ? { 'firstName[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    return (res.data?.data?.data || []).map((e) => ({
      id: e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture,
    }));
  }, []);

  const searchDepartments = useCallback(async (q) => {
    const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/departments', { params });
    return (res.data?.data?.data || []).map((d) => ({ id: d.id, name: d.name }));
  }, []);

  const searchLocations = useCallback(async (q) => {
    const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/locations', { params });
    return (res.data?.data?.data || []).map((l) => ({ id: l.id, name: l.name }));
  }, []);

  const searchUserGroups = useCallback(async (q) => {
    const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/user-groups', { params });
    return (res.data?.data?.data || []).map((g) => ({ id: g.id, name: g.name }));
  }, []);

  const handleAddOpen = () => {
    setSelEmployees([]); setSelDepartments([]); setSelLocations([]); setSelUserGroups([]);
    setDrawerOpen(true);
  };

  const handleAddSave = async () => {
    if (!selEmployees.length && !selDepartments.length && !selLocations.length && !selUserGroups.length) return;
    setSaving(true);
    try {
      await addParticipants(surveyId, {
        employeeIds: selEmployees, departmentIds: selDepartments,
        locationIds: selLocations, userGroupIds: selUserGroups,
      });
      setDrawerOpen(false);
      fetchData(queryParams);
    } catch { /* interceptor */ }
    setSaving(false);
  };

  return (
    <Container>
      <ConfirmDialog
        isOpen={removeConfirm.isOpen}
        title={t('survey.removeParticipant', { defaultValue: 'Katılımcıyı Çıkar' })}
        message={t('survey.removeParticipantMessage', { name: removeConfirm.name, defaultValue: `${removeConfirm.name} katılımcıdan çıkarılacak. Emin misiniz?` })}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setRemoveConfirm({ isOpen: false, userId: null, name: '' })}
        loading={removing}
      />

      {useMemo(() => (
        <DataTable
          title={t('survey.participants', { defaultValue: 'Katılımcılar' })}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canEdit}
          newButtonLabel={t('survey.addParticipant', { defaultValue: 'Katılımcı Ekle' })}
          onNewButtonClick={handleAddOpen}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          tableBodyMaxHeight={containerHeight ? `${containerHeight - 144}px` : undefined}
        />
      ), [
        t, fetching, columns, data, pagination, canEdit,
        filterList, handleFilterConfirm, handleDataStateChange,
        containerHeight,
      ])}

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        direction="right"
        size="45%"
        duration={150}
        overlayOpacity={0.2}
        style={{ display: 'flex', flexDirection: 'column', maxWidth: '100vw', minWidth: 380,
          margin: '12px', borderRadius: '6px', height: 'calc(100vh - 24px)',
          boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)', zIndex: 1000 }}
      >
        <DrawerHeader>
          <DrawerTitle>{t('survey.addParticipant', { defaultValue: 'Katılımcı Ekle' })}</DrawerTitle>
          <CloseBtn onClick={() => setDrawerOpen(false)}><X size={20} /></CloseBtn>
        </DrawerHeader>
        <DrawerBody>
          <AsyncGenericSelect label={t('employee.title')} value={selEmployees} onChange={setSelEmployees}
            onSearch={searchEmployees} renderOption={(o) => <PersonCell name={o.name} profilePicture={o.profilePicture} />} isMulti />
          <AsyncGenericSelect label={t('department.title')} value={selDepartments} onChange={setSelDepartments}
            onSearch={searchDepartments} isMulti />
          <AsyncGenericSelect label={t('location.title')} value={selLocations} onChange={setSelLocations}
            onSearch={searchLocations} isMulti />
          <AsyncGenericSelect label={t('userGroup.title')} value={selUserGroups} onChange={setSelUserGroups}
            onSearch={searchUserGroups} isMulti />
        </DrawerBody>
        <DrawerFooter>
          <Button variant="outline" onClick={() => setDrawerOpen(false)} disabled={saving}>{t('common.cancel')}</Button>
          <Button onClick={handleAddSave} disabled={saving}>{saving ? '...' : t('common.save')}</Button>
        </DrawerFooter>
      </Drawer>
    </Container>
  );
}
